/**
 * The site's glass, in one place.
 *
 * A count of what was here before this file: **eighteen** different recipes for
 * the same idea — borders at `white/6, /8, /10, /12, /[0.12], /[0.14], /15,
 * /20, /25, /40` over fills from `white/[0.02]` to `white/10`, each with its
 * own blur and shadow. Every box was therefore a slightly different piece of
 * glass, which is exactly why they read as unrelated objects scattered on the
 * background instead of one material the page is made of.
 *
 * Two tiers is all this product needs. What makes glass read as glass here is
 * the colour moving BEHIND it (the ambient backdrop), not highlights painted on
 * top — so neither tier carries a specular, an inset highlight or a coloured
 * glow.
 */

/** The default box: a panel resting on the background. */
export const SURFACE = 'border border-white/10 bg-white/[0.04] backdrop-blur-xl'

/**
 * A box that has to sit forward of its neighbours — a modal, the front card of
 * a stack, anything the eye should read as nearer. Same material, more of it.
 */
export const SURFACE_STRONG = 'border border-white/[0.14] bg-white/[0.07] backdrop-blur-xl'

/**
 * A box that OVERLAPS other content rather than resting on the background:
 * a card in a stack, a menu panel, a dropdown. The translucent tiers only work
 * over the ambient backdrop — put one over a paragraph and the paragraph reads
 * straight through it. Same hairline, near-opaque base.
 */
export const SURFACE_SOLID =
  'border border-white/[0.12] bg-[var(--flou-surface-solid)] backdrop-blur-xl'

/**
 * The quiet tier: a row in a list, a fact in a statement, a tile in a grid.
 *
 * A third recipe, added deliberately after the two above, because a list is not
 * a panel repeated forty times. At {@link SURFACE}'s weight a screen of rows
 * reads as a wall of boxes and the rows stop being a list — the eye counts
 * frames instead of scanning names. Half the border and half the fill is what
 * makes forty of them read as one object with divisions in it.
 *
 * It carried no name for a long time and was pasted out by hand in FORTY-NINE
 * places, from the orders list to the skeleton that stands in for it while it
 * loads — which is precisely how a row and its own placeholder end up being
 * drawn in two slightly different greys.
 *
 * Padding and layout are the caller's: what is shared here is the material,
 * not the shape.
 */
export const SURFACE_QUIET = 'rounded-2xl border border-white/[0.07] bg-white/[0.02]'

/**
 * A quiet tile you can press — a row that opens something.
 *
 * The focus ring is part of the token rather than an afterthought at each call
 * site: these rows are the panel's main way of getting anywhere, so a keyboard
 * has to be able to see where it is on every one of them.
 */
export const SURFACE_QUIET_LINK =
  'transition-colors hover:border-white/15 hover:bg-white/[0.05] focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:outline-none'

/**
 * The frame of a panel control — a search box, a select, an empty field.
 *
 * 49px rather than a round number: it is a 44px touch target (the smallest the
 * WCAG target-size rule accepts without an exception) plus the hairline top and
 * bottom, and a control that does not agree with its neighbours on that height
 * shows up as a step in a row of them.
 */
export const PANEL_FIELD = 'h-[49px] rounded-xl border border-white/12 bg-white/[0.03]'

/** Hover state for an interactive surface. Lightens; never changes the border. */
export const SURFACE_HOVER = 'transition-colors duration-200 hover:bg-white/[0.07]'

/**
 * The rental card, as a printed sheet.
 *
 * Every other surface in this file is glass over the page — a tier of white at
 * an alpha, so the background shows through and the box belongs to the dark.
 * This one does not: it is OPAQUE, and it looks the same under either theme.
 *
 * It was white with black and grey type, on the owner's call — "ten panel
 * kafelek ma być biały a tekst na nim czarny i szary, reszta tło ma zostać
 * czarne" — and it is graphite with white type now, on the owner's call again.
 * What survives both is the part that was never about the colour: the sheet is
 * opaque, it is lighter than the window cut into it, and it does not swap with
 * the theme.
 *
 * NOT `bg-white text-neutral-900`, which is the app's existing inverted pair
 * and would have been the obvious reach. Those two tokens are REMAPPED in
 * `@theme inline` so an inverted control stays inverted — white ground on a
 * dark page, dark ground on a light one. That is right for a button and wrong
 * for this: the card would turn black on the light theme, and "biały kafelek"
 * would have lasted exactly as long as nobody pressed the theme toggle. The
 * `--flou-card-*` tokens are deliberately absent from the light block for the
 * same reason.
 *
 * The hairline is what separates the sheet from a light page, since there it
 * cannot be separated by value.
 */
export const SURFACE_SHEET =
  'border border-[var(--flou-card-line)] bg-[var(--flou-card-sheet)] text-[var(--flou-card-ink)]'

/** Type on the sheet: full strength for a name and a price, 62% for the rest. */
export const SHEET_INK = 'text-[var(--flou-card-ink)]'
export const SHEET_INK_QUIET = 'text-[color-mix(in_oklab,var(--flou-card-ink)_62%,transparent)]'

/**
 * The black window a cut-out product stands in.
 *
 * Equipment photography here is transparent PNG/WebP cut-outs, and almost all
 * of it is dark: camera bodies, lens barrels, a shotgun mic. That fact drove
 * three wrong answers before this one. A flat `oklch(0.13)` fill measured
 * DARKER than the page behind it, so a card read as a slab with a hole punched
 * in its top. A radial pool opening at `0.82` gave the silhouette something to
 * read against and bought a near-white HALO behind every photograph — "nie
 * podoba mi się ten white glow za zdjęciami". A top-down seamless fixed the
 * halo and was still a lit backdrop where none was wanted.
 *
 * The window is black now, and what changed is what surrounds it: the card is
 * an opaque sheet lighter than the window, so the picture area no longer has to
 * supply its own contrast to avoid reading as a hole. That sheet was white and
 * is graphite; the window did not move, because `0.14` was measured against the
 * photographs rather than against the card.
 *
 * A flat colour, not a gradient — a gradient behind a photograph is the thing
 * that was wrong twice. Carried as a CSS variable because it is one value used
 * by both the grid card and the item's own page, and the two must not drift:
 * the same lens should not sit on two different blacks.
 */
export const PRODUCT_STAGE = 'var(--flou-stage)'

/**
 * Corner radii, as a scale rather than a per-component guess. The page carried
 * `rounded-2xl`, `rounded-3xl`, `rounded-[26px]` and `rounded-full` side by
 * side; big radii on big boxes read balloon-y.
 */
export const RADIUS = {
  /** Cards, panels, media. */
  box: 'rounded-2xl',
  /** Full-bleed feature blocks. */
  feature: 'rounded-3xl',
  /** Chips, pills, controls. */
  pill: 'rounded-full',
  /**
   * The SWITCH STRIPS on the price pages — the category tabs and the „1 rolka
   * / Pakiet 10 rolek" pair.
   *
   * Not `pill`. Owner, 16.10: *„mniej zaokrąglone te elementy"*. Fully round
   * strips sat oddly over tier cards with a 24px corner, and 14 outside / 10
   * inside is the figure the navigation already uses for its own capsule and
   * links — so the two controls on the page now speak the same language as
   * the bar above them.
   */
  strip: 'rounded-[0.875rem]',
  /** A tab INSIDE `strip`, four pixels tighter so the nest reads as a nest. */
  stripTab: 'rounded-[0.625rem]',
} as const

/**
 * What a CHOSEN option looks like.
 *
 * Two shapes, because a chip and a card want different halves of the same idea:
 *
 * `PICKED_FILL` — the fill IS the answer. For small controls where a ring would
 * be most of the object: pills, calendar days, time slots.
 *
 * `PICKED_RING` — the gradient is the border and the fill stays dark, painted
 * `border-box` over `padding-box` so the glass survives. For anything with
 * content inside it that still has to be readable.
 *
 * Named here rather than pasted because it was already in four files under
 * three slightly different spellings, which is how a system stops being one.
 *
 * ── WHITE, AND THE ARGUMENT AGAINST IT IS RECORDED ───────────────────────
 * `PICKED_FILL` was white, became the brand gradient, and is white again on
 * the owner's call: *„kolorki tych guzików powinny być białe a nie
 * gradientowe, i to wszędzie nie tylko tutaj"*.
 *
 * The case FOR the gradient is kept here rather than deleted, because it was a
 * real one: a white block reads as a disabled control as often as a selected
 * one, and it says nothing about whose site it is. What it loses is what
 * settled it — the gradient is the site's „press this" (every CTA wears it),
 * and spending it on „this one is ticked" left a screen of thumbnail levels
 * where the loudest thing was a state rather than an action. White is quiet,
 * unambiguous, and it never competes with the button that finishes the job.
 *
 * The ink is `neutral-900`, not white: on a solid white pill white text is
 * invisible, and every call site that used to set its own light label reads
 * the colour from here now.
 */
export const PICKED_FILL =
  'border-transparent bg-white text-neutral-900 shadow-[0_10px_28px_-14px_rgba(0,0,0,0.85)]'

export const PICKED_RING =
  'border-transparent [background:linear-gradient(oklch(0.185_0.03_300),oklch(0.16_0.025_300))_padding-box,var(--flou-gradient)_border-box] shadow-[0_28px_90px_-40px_var(--flou-glow-core)]'

/**
 * The light behind a closing call to action.
 *
 * The three "otwórz kreator" / "poproś o wycenę" bands first got the product
 * well for this — the pale studio sweep that used to sit UNDER a black cut-out
 * camera, opening at `oklch(0.82 …)`. Stretched across a full-width panel with
 * a heading and a paragraph on it, it read as fog: a milky grey slab, lighter
 * in the middle than the white text sitting on it. (That sweep is gone
 * entirely now — see {@link PRODUCT_STAGE} — but the lesson it taught here
 * stands.)
 *
 * What a closing band actually wants is a glow where its button is. This is the
 * brand's own magenta at a tenth of its strength, pooled at the right edge
 * behind the control, falling to nothing before it reaches the copy on the
 * left. On a phone, where the button wraps underneath, the pool is simply off
 * to one side of both — never behind the words.
 */
export const CTA_BLOOM =
  'radial-gradient(58% 180% at 92% 50%, oklch(0.62 0.26 330 / 0.22), oklch(0.5 0.2 300 / 0.07) 42%, transparent 66%)'
